import React from 'react';
import { Typography, Container, Box, Grid, Paper, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeProvider';
import {
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Code as CodeIcon,
  Language as LanguageIcon,
  BookOutlined as BookOutlinedIcon,
} from '@mui/icons-material';

// Define the documentation guides available
const guides = [
  {
    id: 'lis-integration',
    title: 'LIS Integration Guide',
    description: 'Learn how to integrate your Laboratory Information System (LIS) with our API',
    icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
    path: '/lis-portal/integration-guide'
  },
  {
    id: 'vendor-api',
    title: 'Vendor API Guide',
    description: 'Comprehensive documentation for all Vendor API endpoints and authentication',
    icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
    path: '/lis-portal/vendor-api-guide'
  },
  {
    id: 'api-examples',
    title: 'API Code Examples',
    description: 'Code examples for integrating with our API in multiple languages',
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    path: '/lis-portal/examples'
  },
  {
    id: 'reports-guide',
    title: 'Reports Guide',
    description: 'Understanding the different reports available and how to interpret them',
    icon: <BookOutlinedIcon sx={{ fontSize: 40 }} />,
    path: '/lis-portal/reports-guide'
  },
  {
    id: 'support-information',
    title: 'Support & Resources',
    description: 'Additional resources and support contact information',
    icon: <LanguageIcon sx={{ fontSize: 40 }} />,
    path: '/lis-portal/support'
  }
];

const DocumentationGuidePage: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" color="primary" gutterBottom sx={{ mb: 4 }}>
        API Documentation & Guides
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4 }}>
        Welcome to our documentation center. Here you'll find comprehensive guides and resources to help you 
        integrate with our systems, understand our APIs, and make the most of our services.
      </Typography>
      
      <Grid container spacing={3}>
        {guides.map((guide) => (
          <Grid item xs={12} md={6} key={guide.id}>
            <Paper 
              elevation={3}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer'
                },
                borderLeft: `4px solid ${colors.primary.main}`
              }}
              onClick={() => navigate(guide.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  color: colors.primary.main, 
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {guide.icon}
                </Box>
                <Typography variant="h5" component="h2">
                  {guide.title}
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ mb: 3, flex: 1 }}>
                {guide.description}
              </Typography>
              
              <Button 
                variant="outlined" 
                color="primary" 
                sx={{ alignSelf: 'flex-start' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(guide.path);
                }}
              >
                View Guide
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 6, p: 3, bgcolor: colors.background.alt, borderRadius: 1 }}>
        <Typography variant="h5" color="primary" gutterBottom>
          Need Help?
        </Typography>
        <Typography variant="body1">
          If you can't find what you're looking for or need additional assistance, please contact our support team at 
          <a href="mailto:support@reasonshield.com" style={{ color: colors.primary.main, marginLeft: '4px' }}>
            support@reasonshield.com
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default DocumentationGuidePage; 